import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  CheckCircle2,
  Eye,
  GitPullRequestArrow,
  PencilLine,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type {
  JacintaMemoryEntry,
  MemoryCategory,
  MemorySource,
} from '@/modules/shared/types/yc-demo';

const CATEGORY_LABEL: Record<MemoryCategory, string> = {
  scoring: 'Scoring',
  sourcing: 'Sourcing',
  pipeline: 'Pipeline',
  culture: 'Culture',
  compensation: 'Compensation',
};

const SOURCE_LABEL: Record<MemorySource, string> = {
  score_edit: 'via score edit',
  pipeline_outcome: 'via pipeline outcome',
  observed: 'via observation',
  manual: 'added manually',
};

const SOURCE_ICON: Record<MemorySource, LucideIcon> = {
  score_edit: PencilLine,
  pipeline_outcome: GitPullRequestArrow,
  observed: Eye,
  manual: Sparkles,
};

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function avatarTint(name: string): string {
  // Stable colour per name — pulled from the warm-gray + accent palette.
  const palette = ['#c8c3ba', '#9c8e7c', '#7a604f', '#b89580', '#a8a59c', '#8b7e6b', '#cfa48a'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(hash) % palette.length];
}

export function MemoryCard({
  entry,
  isFresh,
}: {
  entry: JacintaMemoryEntry;
  isFresh?: boolean;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showInitials = !entry.attributedTo.avatarUrl || imgFailed;
  const SourceIcon = SOURCE_ICON[entry.source];

  return (
    <motion.article
      layout
      initial={isFresh ? { opacity: 0, y: -16 } : false}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className={isFresh ? 'memory-card memory-card-fresh' : 'memory-card'}
    >
      <header className="memory-card-header">
        <span
          className="memory-avatar"
          style={showInitials ? { background: avatarTint(entry.attributedTo.name) } : undefined}
        >
          {showInitials ? (
            <span className="memory-avatar-initials">{initials(entry.attributedTo.name)}</span>
          ) : (
            <img
              src={entry.attributedTo.avatarUrl}
              alt=""
              onError={() => setImgFailed(true)}
              draggable={false}
            />
          )}
        </span>
        <div className="memory-attribution">
          <span className="memory-author">{entry.attributedTo.name}</span>
          <span className="memory-author-meta">
            {entry.attributedTo.role}
            <span className="memory-dot" aria-hidden> · </span>
            <time dateTime={entry.createdAt} title={new Date(entry.createdAt).toLocaleString()}>
              {formatRelativeTime(entry.createdAt)}
            </time>
          </span>
        </div>
      </header>

      <div className="memory-trigger">{entry.trigger}</div>
      <p className="memory-insight">{entry.insight}</p>

      <footer className="memory-card-footer">
        <span className="memory-pill memory-pill-applied">
          <CheckCircle2 size={12} strokeWidth={1.8} />
          Applied {entry.appliedCount.toLocaleString()} {entry.appliedCount === 1 ? 'time' : 'times'}
        </span>
        <span className={`memory-pill memory-pill-category memory-cat-${entry.category}`}>
          {CATEGORY_LABEL[entry.category]}
        </span>
        <span className="memory-pill memory-pill-source">
          <SourceIcon size={12} strokeWidth={1.8} />
          {SOURCE_LABEL[entry.source]}
        </span>
      </footer>
    </motion.article>
  );
}
